
function ctlRecommendationFetchUrl(pathOrUrl) {
  if (!pathOrUrl) return "";
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  const root = (window.Shopify?.routes?.root ?? "/").replace(/\/+$/, "") || "";
  const path = pathOrUrl.startsWith("/") ? pathOrUrl : `/${pathOrUrl}`;
  return root ? `${root}${path}` : path;
}

class CompleteTheLookRecommendations extends HTMLElement {
  /**
   * After injecting cards, stamp data-cards-loaded on every .card-loading that
   * already has data-intersected (set by hydrateProductCard when it couldn't
   * find the card template yet). If the template isn't in the DOM yet, watch
   * for it and stamp when it appears.
   */
  _stampCardsLoaded(container, cardClass) {
    const stamp = () => {
      container
        .querySelectorAll(".card-loading[data-intersected]")
        .forEach((el) => el.setAttribute("data-cards-loaded", "true"));
    };

    if (document.querySelector(`[data-product-card='${cardClass}']`)) {
      stamp();
      return;
    }

    // Card template not in DOM yet — watch for it.
    const mo = new MutationObserver(() => {
      if (document.querySelector(`[data-product-card='${cardClass}']`)) {
        mo.disconnect();
        stamp();
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });

    // Fallback: if window.modalsLoaded fires before MutationObserver, stamp then.
    const onModalsLoaded = () => {
      mo.disconnect();
      stamp();
    };
    if (window.modalsLoaded) {
      onModalsLoaded();
    } else {
      Object.defineProperty(window, "modalsLoaded", {
        configurable: true,
        set(val) {
          Object.defineProperty(window, "modalsLoaded", {
            configurable: true,
            writable: true,
            value: val,
          });
          if (val) onModalsLoaded();
        },
        get() {
          return undefined;
        },
      });
    }
  }

  connectedCallback() {
    const observer = new IntersectionObserver(
      ([entry], obs) => {
        if (!entry.isIntersecting) return;
        obs.unobserve(this);
        const url = ctlRecommendationFetchUrl(this.dataset.url);
        if (!url) return;
        fetch(url)
          .then((r) => r.text())
          .then((text) => {
            const doc = document.createElement("div");
            doc.innerHTML = text;
            const inner = doc.querySelector("[data-complete-the-look-recs]");
            if (!inner || !inner.innerHTML.trim()) return;

            const scroll = this.querySelector("[data-ctl-scroll]");
            if (!scroll) return;

            // Inject the server-rendered recommendation cards
            scroll.innerHTML = inner.innerHTML;

            const displayLimit = Math.max(
              1,
              Math.min(
                10,
                parseInt(this.dataset.displayLimit, 10) || 4
              )
            );
            while (scroll.children.length > displayLimit) {
              scroll.lastElementChild?.remove();
            }

            // Boot Alpine on the freshly injected markup
            if (window.Alpine?.initTree) window.Alpine.initTree(scroll);

            // hydrateProductCard waits for [data-product-card] in the DOM.
            // If the card template hasn't loaded yet, newly injected .card-loading
            // elements won't have data-cards-loaded set (that only runs on elements
            // present at load time). Stamp it ourselves once the template exists.
            const cardClass =
              this.dataset.productCardClass?.trim() || "product-card";
            this._stampCardsLoaded(scroll, cardClass);

            const grid = this.querySelector("[data-grid-slider]");
            const stack = grid?._x_dataStack?.[0];
            if (stack) {
              stack.scrollEl = scroll;
              stack.updateNav?.();
              requestAnimationFrame(() => stack.updateNav?.());
            }
          })
          .catch((e) => console.error("CTL recommendations fetch:", e));
      },
      { rootMargin: "0px 0px 300px 0px" }
    );
    observer.observe(this);
  }
}

if (!customElements.get("complete-the-look-recommendations")) {
  customElements.define(
    "complete-the-look-recommendations",
    CompleteTheLookRecommendations
  );
}
